import type { z } from 'zod';
import { publicEnvSchema, serverEnvSchema } from './env-schema';

function validatePublicEnv(): z.infer<typeof publicEnvSchema> {
  const result = publicEnvSchema.safeParse({
    NEXT_PUBLIC_API_URL_LOCAL: process.env.NEXT_PUBLIC_API_URL_LOCAL,
    NEXT_PUBLIC_API_URL_DEVNET: process.env.NEXT_PUBLIC_API_URL_DEVNET,
    NEXT_PUBLIC_API_URL_MAINNET: process.env.NEXT_PUBLIC_API_URL_MAINNET,
    NEXT_PUBLIC_NATS_URL_LOCAL: process.env.NEXT_PUBLIC_NATS_URL_LOCAL,
    NEXT_PUBLIC_NATS_URL_DEVNET: process.env.NEXT_PUBLIC_NATS_URL_DEVNET,
    NEXT_PUBLIC_NATS_URL_MAINNET: process.env.NEXT_PUBLIC_NATS_URL_MAINNET,
    NEXT_PUBLIC_RPC_URL_LOCAL: process.env.NEXT_PUBLIC_RPC_URL_LOCAL,
    NEXT_PUBLIC_RPC_URL_DEVNET: process.env.NEXT_PUBLIC_RPC_URL_DEVNET,
    NEXT_PUBLIC_RPC_URL_MAINNET: process.env.NEXT_PUBLIC_RPC_URL_MAINNET,
    NEXT_PUBLIC_PROGRAM_ID_LOCAL: process.env.NEXT_PUBLIC_PROGRAM_ID_LOCAL,
    NEXT_PUBLIC_PROGRAM_ID_DEVNET: process.env.NEXT_PUBLIC_PROGRAM_ID_DEVNET,
    NEXT_PUBLIC_PROGRAM_ID_MAINNET: process.env.NEXT_PUBLIC_PROGRAM_ID_MAINNET,
    NEXT_PUBLIC_API_KEY_LOCAL: process.env.NEXT_PUBLIC_API_KEY_LOCAL,
    NEXT_PUBLIC_API_KEY_DEVNET: process.env.NEXT_PUBLIC_API_KEY_DEVNET,
    NEXT_PUBLIC_API_KEY_MAINNET: process.env.NEXT_PUBLIC_API_KEY_MAINNET,
    NEXT_PUBLIC_OAUTH_REDIRECT_URI: process.env.NEXT_PUBLIC_OAUTH_REDIRECT_URI,
    NEXT_PUBLIC_STATSIG_CLIENT_KEY: process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY,
    NEXT_PUBLIC_APPSFLYER_ONELINK_URL: process.env.NEXT_PUBLIC_APPSFLYER_ONELINK_URL,
    NEXT_PUBLIC_BETTERSTACK_JS_TOKEN: process.env.NEXT_PUBLIC_BETTERSTACK_JS_TOKEN,
    NEXT_PUBLIC_DEV_TOOLKIT: process.env.NEXT_PUBLIC_DEV_TOOLKIT,
    NEXT_PUBLIC_AGENT_REACT_DEVTOOLS: process.env.NEXT_PUBLIC_AGENT_REACT_DEVTOOLS,
    NEXT_PUBLIC_MORFI_BLOCKHASH_RESOLVER_MODE:
      process.env.NEXT_PUBLIC_MORFI_BLOCKHASH_RESOLVER_MODE,
    NEXT_PUBLIC_TURNKEY_ORG_ID: process.env.NEXT_PUBLIC_TURNKEY_ORG_ID,
    NEXT_PUBLIC_TURNKEY_API_BASE_URL: process.env.NEXT_PUBLIC_TURNKEY_API_BASE_URL,
    NEXT_PUBLIC_TURNKEY_AUTH_PROXY_CONFIG_ID: process.env.NEXT_PUBLIC_TURNKEY_AUTH_PROXY_CONFIG_ID,
    NEXT_PUBLIC_INTERCOM_APP_ID: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
    NEXT_PUBLIC_CUSTOMERIO_CDP_WRITE_KEY: process.env.NEXT_PUBLIC_CUSTOMERIO_CDP_WRITE_KEY,
    NEXT_PUBLIC_CUSTOMERIO_SITE_ID: process.env.NEXT_PUBLIC_CUSTOMERIO_SITE_ID,
    NEXT_PUBLIC_CANNY_APP_ID: process.env.NEXT_PUBLIC_CANNY_APP_ID,
    NEXT_PUBLIC_DONATION_RECIPIENT_MAINNET: process.env.NEXT_PUBLIC_DONATION_RECIPIENT_MAINNET,
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,
    NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION: process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION,
    NEXT_PUBLIC_BING_SITE_VERIFICATION: process.env.NEXT_PUBLIC_BING_SITE_VERIFICATION,
    NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
    NEXT_PUBLIC_DEV_PROXY: process.env.NEXT_PUBLIC_DEV_PROXY,
  });

  if (!result.success) {
    const formatted = result.error.flatten().fieldErrors;
    throw new Error(`Invalid environment variables:\n${JSON.stringify(formatted, null, 2)}`);
  }

  return result.data;
}

function validateServerEnv(): z.infer<typeof serverEnvSchema> {
  const result = serverEnvSchema.safeParse({
    VERCEL_ENV: process.env.VERCEL_ENV,
    STATSIG_CONSOLE_API_KEY: process.env.STATSIG_CONSOLE_API_KEY,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    MAINNET_RPC_URL: process.env.MAINNET_RPC_URL,
    TASKON_CHECK_SECRET: process.env.TASKON_CHECK_SECRET ?? process.env.WAITLIST_CHECK_SECRET,
    TASKON_BEARER_TOKEN: process.env.TASKON_BEARER_TOKEN,
  });

  if (!result.success) {
    const formatted = result.error.flatten().fieldErrors;
    throw new Error(`Invalid server environment variables:\n${JSON.stringify(formatted, null, 2)}`);
  }

  return result.data;
}

export const env: z.infer<typeof publicEnvSchema> = validatePublicEnv();
export const serverEnv: z.infer<typeof serverEnvSchema> = validateServerEnv();
export { publicEnvSchema, serverEnvSchema };
